"use client";
import { Card } from "@/components/ui/card";
import { MapPin, Bed, Bath, Car, Ruler } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useTenantContext } from '@/contexts/TenantContext';
import { buildPropertyUrl } from '@/lib/property-url';
import MatchScoreBadge from '@/features/leads/MatchScoreBadge';
import { ARCHIVE_REASON_LABELS, type ArchiveReason } from './archive-constants';

const STATUS_LABELS: Record<string, string> = {
  inactive: 'Inativo',
  inativo: 'Inativo',
  archived: 'Arquivado',
  sold: 'Vendido',
  rented: 'Alugado',
  draft: 'Rascunho',
  pending: 'Pendente',
};

interface ImovelCardProps {
  imovel: {
    id: string;
    title: string;
    description?: string | null;
    price: number;
    property_code?: string | null;
    property_type?: string | null;
    images?: string[] | null;
    transaction_type?: string | null;
    status?: string | null;
    bedrooms?: number | null;
    bathrooms?: number | null;
    parking_spaces?: number | null;
    total_area?: number | null;
    built_area?: number | null;
    area_unit?: string | null;
    built_area_unit?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
    building_name?: string | null;
    archive_reason?: string | null;
    sold_price?: number | null;
  };
  priority?: boolean;
  /** Score de compatibilidade (0–100) — exibe badge quando informado (matching x lead). */
  matchScore?: number;
}

export default function ImovelCard({ imovel, priority = false, matchScore }: ImovelCardProps) {
  // ✅ Usar contexto da Tenant para isolamento
  const { tenant } = useTenantContext();
  const config = tenant?.configurations || {};

  // ✅ Aplicar cores personalizadas do tenant
  const textStyle = {
    color: config.cor_texto || '#2D3748'
  };

  const buttonStyle = {
    backgroundColor: config.cor_primaria || '#232B3B', // Usar Cor Primária (#232B3B)
    color: 'white',
    '--tw-hover-bg-opacity': '0.8'
  } as React.CSSProperties;

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(price);

  const AREA_UNIT_FACTORS: Record<string, number> = { m2: 1, ha: 10000, alq: 48400 };
  const AREA_UNIT_LABELS: Record<string, string> = { m2: 'm²', ha: 'ha', alq: 'alq' };
  const areaRaw = imovel.built_area || imovel.total_area;
  const areaUnitKey = (imovel.built_area ? imovel.built_area_unit : imovel.area_unit) || 'm2';
  const area = areaRaw;
  const areaFormatted = areaRaw && Number(areaRaw) > 0
    ? (() => {
        const factor = AREA_UNIT_FACTORS[areaUnitKey] ?? 1;
        const label = AREA_UNIT_LABELS[areaUnitKey] ?? 'm²';
        const converted = Number(areaRaw) / factor;
        return `${converted % 1 === 0 ? converted.toLocaleString('pt-BR') : converted.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ${label}`;
      })()
    : null;
  const bairroCidadeEstado = [imovel.neighborhood, imovel.city, imovel.state].filter(Boolean).join(' - ');

  return (
    <div className="h-full transition-transform duration-300 ease-out hover:scale-[1.02]">
      <Link
        href={buildPropertyUrl(imovel)}
        className="block h-full"
        aria-label={`Ver detalhes do imóvel ${imovel.title} - ${formatPrice(imovel.price)} em ${bairroCidadeEstado || 'localização não informada'}`}
      >
        <Card
          data-testid="imovel-card"
          className="overflow-hidden rounded-xl shadow-lg border bg-white flex flex-col h-full group cursor-pointer hover:shadow-2xl transition-shadow duration-300 w-full max-w-full"
        >
          <div className="relative aspect-[4/3] w-full overflow-hidden">
            {imovel.images && imovel.images.length > 0 && imovel.images[0] ? (
              <div className="relative w-full h-full transition-transform duration-700 ease-out group-hover:scale-110">
                <Image
                  src={imovel.images[0]}
                  alt={imovel.title}
                  fill
                  className="object-cover"
                  sizes="(max-width: 767px) calc(100vw - 32px), (max-width: 1023px) calc(50vw - 28px), (max-width: 1279px) calc(33vw - 27px), calc(25vw - 26px)"
                  quality={priority ? 80 : 70}
                  priority={priority}
                  fetchPriority={priority ? "high" : undefined}
                  loading={priority ? undefined : "lazy"}
                  placeholder="blur"
                  blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                />
              </div>
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">Sem imagem</div>
            )}
            {(typeof matchScore === 'number' || imovel.status === 'archived') && (
              <div className="absolute top-3 left-3 z-20 flex flex-col items-start gap-1.5">
                {typeof matchScore === 'number' && <MatchScoreBadge score={matchScore} />}
                {/* Imóvel arquivado (ficha do lead): mantém a referência visível
                    com o desfecho em vez de sumir/quebrar */}
                {imovel.status === 'archived' && (
                  <span className="text-xs font-semibold px-3 py-1 rounded-full shadow bg-amber-500/95 text-white backdrop-blur-sm">
                    {ARCHIVE_REASON_LABELS[(imovel.archive_reason ?? '') as ArchiveReason] ?? 'Arquivado'}
                    {imovel.sold_price != null && imovel.archive_reason?.startsWith('VENDIDO')
                      ? ` · ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(imovel.sold_price)}`
                      : ''}
                  </span>
                )}
              </div>
            )}
            <div className="absolute top-3 right-3 z-20">
              <span
                className="text-xs font-semibold px-3 py-1 rounded-full shadow backdrop-blur-sm"
                style={{ background: '#FFFFFFE6', color: config.cor_primaria || '#1B365D' }}
              >
                {imovel.transaction_type
                  ? imovel.transaction_type.charAt(0).toUpperCase() + imovel.transaction_type.slice(1).toLowerCase()
                  : ''}
              </span>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 w-full p-4 z-10">
              <h2 className="text-xl font-bold text-white drop-shadow mb-1 truncate">{imovel.title}</h2>
              <div className="flex items-center text-white text-sm">
                <MapPin className="w-4 h-4 mr-1" aria-hidden="true" />
                <span className="truncate">{bairroCidadeEstado || '-'}</span>
              </div>
            </div>
          </div>
          <div className="p-3 sm:p-4 flex flex-col flex-1">
            {imovel.property_code && (
              <div className="flex justify-end mb-1">
                <span className="inline-block text-xs text-[#374151] font-mono">#{imovel.property_code}</span>
              </div>
            )}
            <span className="text-2xl sm:text-3xl font-bold mb-1" style={textStyle}>{formatPrice(imovel.price)}</span>
            <div className="flex items-center gap-3 text-base mb-1" style={textStyle}>
              <div className="flex items-center"><Bed className="w-5 h-5 mr-1" aria-hidden="true" />{imovel.bedrooms ?? '-'}</div>
              <div className="flex items-center"><Bath className="w-5 h-5 mr-1" aria-hidden="true" />{imovel.bathrooms ?? '-'}</div>
              <div className="flex items-center"><Car className="w-5 h-5 mr-1" aria-hidden="true" />{imovel.parking_spaces ?? '-'}</div>
              <div className="flex items-center"><Ruler className="w-5 h-5 mr-1" aria-hidden="true" />{areaFormatted ? <span>{areaFormatted}</span> : '-'}</div>
            </div>
            {imovel.building_name && (
              <p className="text-xs text-[#374151] mb-1 truncate">{imovel.building_name}</p>
            )}
            {imovel.description && (
              <p className="text-sm line-clamp-2 mb-1" style={textStyle}>{imovel.description}</p>
            )}
            <div className="flex-1" />
            <div className="flex justify-between items-center mt-auto">
              {imovel.status && imovel.status !== 'active' && (
                <span className="inline-block text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                  {STATUS_LABELS[imovel.status] ?? imovel.status}
                </span>
              )}
            </div>
            <div className="flex justify-center mt-4">
              <div className="w-full transition-transform duration-200 ease-out hover:scale-[1.02]">
                <div
                  className="block w-full text-center font-semibold rounded-lg py-2 px-3 sm:px-4 transition-all duration-300 pointer-events-none"
                  style={buttonStyle}
                  aria-hidden="true"
                >
                  Ver Detalhes
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Link>
    </div>
  );
}
